if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        const formData = new FormData(this.form);

        let cartData;
        const shadowVariants = this.closest('product-info').dataset.shadowVariants;
        if (shadowVariants) {
          // shadwoVariants is a string in this format variantId|quantity,variantId|quantity. Parse it and add it to the cart. To each item add a computed attribute, bundle_id which will be variant_id:timestamp the variant id is the one from the form. Compute the bundle_id at the beginning and reuse it to avoid issues with the timestamp

          const bundleId = `${formData.get('id')}:${Date.now()}`;
          const selectedQuantity = Number.parseInt(formData.get('quantity')) || 1;

          const items = shadowVariants.split(',').map((item) => {
            const [id, quantity] = item.split('|');
            return {
              id: Number.parseInt(id),
              quantity: Number.parseInt(quantity) * selectedQuantity,
              properties: { _bundle_id: bundleId, _modular_parent_title: this.closest('product-info').dataset.title, _modular_parent_image: this.closest('product-info').dataset.image },
            };
          });

          cartData = { items };
        } else {
          cartData = {
            items: [
              {
                id: Number.parseInt(formData.get('id')),
                quantity: Number.parseInt(formData.get('quantity')),
              },
            ],
          };
        }

        const additionalFieldName = this.dataset.additionalFieldName;
        const additionalFieldValue = this.dataset.additionalFieldValue;

        if (!shadowVariants) {
          if (additionalFieldName && additionalFieldValue) {
            cartData.items[0].properties = { [additionalFieldName]: additionalFieldValue };
          }
        } else {
          cartData.items.forEach((item) => {
            if (additionalFieldName && additionalFieldValue) {
              item.properties = {
                [`_${additionalFieldName}`]: additionalFieldValue,
                [`${additionalFieldName}`]: additionalFieldValue,
                ...item.properties,
              };
            }
          });
        }

        if (this.cart) {
          cartData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          cartData.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const additionalItems = this.closest('product-info')?.querySelector('additional-items');
        if (additionalItems) {
          const state = additionalItems.getState();
          Object.entries(state).forEach(([id, quantity]) => {
            if (Number.parseInt(quantity) > 0) {
              cartData.items.push({
                id: Number.parseInt(id),
                quantity: Number.parseInt(quantity),
              });
            }
          });
        }

        config.body = JSON.stringify(cartData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
              
              // Check A/B test flag for atc_confirmation
              const isBannerVariant = this.checkATCConfirmationFlag() === 'banner';
              
              if (isBannerVariant) {
                // Show add to cart success banner
                this.showAddToCartBanner();
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    // Check A/B test flag - only open drawer if it's the flyout variant
                    const atcVariant = this.checkATCConfirmationFlag();
                    const shouldOpenDrawer = atcVariant === 'flyout' && window.themeSettings?.openCartDrawerOnAdd !== false;
                    this.cart.renderContents(response, shouldOpenDrawer);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Check A/B test flag - only open drawer if it's the flyout variant
              const atcVariant = this.checkATCConfirmationFlag();
              const shouldOpenDrawer = atcVariant === 'flyout' && window.themeSettings?.openCartDrawerOnAdd !== false;
              this.cart.renderContents(response, shouldOpenDrawer);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      checkATCConfirmationFlag() {
        // Check body classes for A/B test flag
        // Format: ab-test--atc_confirmation--[variant]
        const bodyClasses = document.body.className;
        const match = bodyClasses.match(/ab-test--atc_confirmation--(\w+)/);
        
        if (match && match[1]) {
          return match[1]; // Returns 'banner' or 'flyout'
        }
        
        // Default to flyout if no flag is set
        return 'flyout';
      }

      showAddToCartBanner() {
        const banner = document.getElementById('add-to-cart-banner');
        if (!banner || !banner.show) return;

        // Get product title from data attribute
        const productTitle = this.dataset.productTitle || 'Product';
        
        // Show the banner using the custom element's method
        banner.show(productTitle);
      }
    }
  );
}

class ProductContainer extends HTMLElement {
  prevState = null;
  constructor() {
    super();
    this.gallery = this.querySelector('.product__media-wrapper slider-component');
    this.targetElement = this.querySelector('variant-selects details:first-of-type');
    this.absoluteContainer = this.querySelector('.product__media-wrapper');
    this.productDataElement = this.querySelector('.product');

    // Initialize product specs toggle
    this.initProductSpecsToggle();
  }

  connectedCallback() {
    this.updateGalleryStickiness();
    window.addEventListener('resize', this.updateGalleryStickiness.bind(this));
    window.addEventListener('resize', this.handleSpecsResize.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateGalleryStickiness.bind(this));
    window.removeEventListener('resize', this.handleSpecsResize.bind(this));

    // Clean up specs dialog if it exists
    if (this.specsMobileDialog) {
      this.specsMobileDialog.remove();
      this.specsMobileDialog = null;
    }

    // Reset body overflow in case component is removed while dialog is open
    document.body.style.overflow = '';
  }

  initProductSpecsToggle() {
    this.isMobile = window.innerWidth < 768;
    // Search within the current component's section to avoid conflicts
    const section = this.closest('section') || document;
    this.specButtons = section.querySelectorAll('[data-product-spec-buttons] button:not([data-close-specs])');
    this.titleCloseButton = section.querySelector('[data-close-specs]');
    this.productContentBody = section.querySelector('.product-content-body');
    this.specsContainer = section.querySelector('.product__specs-container');
    this.closeButton = this.specsContainer?.querySelector('[data-close]');
    this._keyboardOpen = false;
    this._lastTrigger = null;
    this._activeButton = null;

    if (this.isSingleVariantProduct() && this.specsContainer) {
      // For single variant products, set Dimensions as active by default
      const dimensionsButton = Array.from(this.specButtons).find(
        btn => btn.getAttribute('data-button-type') === 'dimensions'
      );
      
      if (dimensionsButton) {
        // Set button as active
        dimensionsButton.classList.add('is-active');
        this._activeButton = dimensionsButton;
        
        // Hide close button for single variants
        if (this.titleCloseButton) {
          this.titleCloseButton.style.display = 'none';
        }
        
        // Filter content to show only dimensions
        this.filterSpecsContent('dimensions');
        
        // Make sure specs container is visible
        this.specsContainer.style.display = 'block';
        this.specsContainer.style.opacity = '1';
      }
    } else {
      // Hide specs container by default for other products
      if (this.specsContainer) {
        this.specsContainer.style.display = 'none';
      }
    }

    // Add event listeners
    this.addSpecsEventListeners();

    // Expose methods for other components to use
    this.showSpecs = this.toggleSpecs.bind(this);
    this.hideSpecs = this.hideSpecs.bind(this);
  }

  addSpecsEventListeners() {
    // Remove existing listeners to avoid duplicates
    if (this.closeButtonHandler) {
      this.closeButton?.removeEventListener('click', this.closeButtonHandler);
    }

    // Remove existing button listeners if they exist
    if (this._buttonClickHandlers) {
      this.specButtons.forEach((button, index) => {
        if (this._buttonClickHandlers[index]) {
          button.removeEventListener('click', this._buttonClickHandlers[index]);
        }
        if (this._buttonKeydownHandlers[index]) {
          button.removeEventListener('keydown', this._buttonKeydownHandlers[index]);
        }
      });
    }
    
    // Remove title close button listener if it exists
    if (this._titleCloseHandler && this.titleCloseButton) {
      this.titleCloseButton.removeEventListener('click', this._titleCloseHandler);
    }

    this.closeButtonHandler = this.hideSpecs.bind(this);
    this._buttonClickHandlers = [];
    this._buttonKeydownHandlers = [];

    // Add listeners to each button
    this.specButtons.forEach((button, index) => {
      const clickHandler = () => {
        const buttonType = button.getAttribute('data-button-type');
        this._lastTrigger = button;
        this._keyboardOpen = false;
        
        // If same button is active, hide specs
        if (this._activeButton === button && button.classList.contains('is-active')) {
          this.hideSpecs();
        } else {
          // Show specs with specific content
          this.showSpecsForType(buttonType);
          
          // Update active button
          this.specButtons.forEach(btn => btn.classList.remove('is-active'));
          button.classList.add('is-active');
          this._activeButton = button;
          
          // Show close button (but not for single variants)
          if (this.titleCloseButton && !this.isSingleVariantProduct()) {
            this.titleCloseButton.style.display = 'flex';
          }
        }
      };
      
      this._buttonClickHandlers[index] = clickHandler;
      button.addEventListener('click', clickHandler);

      const keydownHandler = (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          const buttonType = button.getAttribute('data-button-type');
          this._lastTrigger = button;
          this._keyboardOpen = true;
          
          // If same button is active, hide specs
          if (this._activeButton === button && button.classList.contains('is-active')) {
            this.hideSpecs();
          } else {
            // Show specs with specific content
            this.showSpecsForType(buttonType);
            
            // Update active button
            this.specButtons.forEach(btn => btn.classList.remove('is-active'));
            button.classList.add('is-active');
            this._activeButton = button;
            
            // Show close button (but not for single variants)
            if (this.titleCloseButton && !this.isSingleVariantProduct()) {
              this.titleCloseButton.style.display = 'flex';
            }
          }
        }
      };
      
      this._buttonKeydownHandlers[index] = keydownHandler;
      button.addEventListener('keydown', keydownHandler);
    });

    // Add listener to title close button
    if (this.titleCloseButton) {
      this._titleCloseHandler = () => {
        this.hideSpecs();
      };
      this.titleCloseButton.addEventListener('click', this._titleCloseHandler);
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeButtonHandler);
    }
  }

  handleSpecsResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const newIsMobile = window.innerWidth < 768;

      if (newIsMobile !== this.isMobile) {
        this.isMobile = newIsMobile;

        // Reset state when switching between mobile and desktop
        if (this.specsMobileDialog) {
          this.specsMobileDialog.remove();
          this.specsMobileDialog = null;
        }

        // Reset body overflow that might be set on mobile
        document.body.style.overflow = '';

        // For single variant products, maintain the current state
        if (this.isSingleVariantProduct()) {
          // Keep the current active state for single variants
          return;
        }

        // Reset all button states for multi-variant products
        this.specButtons.forEach(btn => btn.classList.remove('is-active'));
        this._activeButton = null;

        if (this.productContentBody) {
          this.productContentBody.style.display = 'block';
          this.productContentBody.style.opacity = '1';
        }

        if (this.specsContainer) {
          this.specsContainer.style.display = 'none';
          this.specsContainer.style.opacity = '0';
        }
      }
    }, 250); // 250ms debounce delay
  }

  isSingleVariantProduct() {
    const productElement = this.querySelector('.product');
    const isSingleVariant = productElement?.getAttribute('data-variants-count') === '1';
    const hasNoComboOption = !productElement?.hasAttribute('data-has-combined-option');
    return isSingleVariant && hasNoComboOption;
  }

  showSpecsForType(type) {
    // Store the current type for filtering
    this._currentSpecType = type;
    
    // For single variant products, always use desktop behavior
    if (this.isSingleVariantProduct()) {
      this.showDesktopSpecs(type);
    } else if (this.isMobile) {
      this.showMobileSpecs(type);
    } else {
      this.showDesktopSpecs(type);
    }
  }

  toggleSpecs() {
    // For single variant products, always use desktop behavior
    if (this.isSingleVariantProduct()) {
      this.showDesktopSpecs();
    } else if (this.isMobile) {
      this.showMobileSpecs();
    } else {
      this.showDesktopSpecs();
    }
  }

  showDesktopSpecs(type) {
    // Fade out product content body
    if (this.productContentBody) {
      this.productContentBody.parentElement.style.opacity = '0';
      setTimeout(() => {
        this.productContentBody.parentElement.style.display = 'none';

        // Fade in specs container
        if (this.specsContainer) {
          // Apply filtering based on type
          this.filterSpecsContent(type);
          
          this.specsContainer.style.display = 'block';
          setTimeout(() => {
            this.specsContainer.style.opacity = '1';

            // Only focus close button if opened via keyboard
            if (this._keyboardOpen) {
              const closeBtn = this.specsContainer.querySelector('[data-close]');
              if (closeBtn) {
                closeBtn.focus();
              }
            }
          }, 10);
        }
      }, 300); // Match transition duration
    }
  }

  hideDesktopSpecs() {
    // Fade out specs container
    if (this.specsContainer) {
      this.specsContainer.style.opacity = '0';
      setTimeout(() => {
        this.specsContainer.style.display = 'none';

        // Fade in product content body
        if (this.productContentBody) {
          this.productContentBody.parentElement.style.display = 'block';
          setTimeout(() => {
            this.productContentBody.parentElement.style.opacity = '1';

            // Return focus to spec button only if opened via keyboard
            if (this._keyboardOpen && this._lastTrigger) {
              setTimeout(() => {
                this._lastTrigger.focus();
              }, 0);
            }
          }, 10);
        }
      }, 300); // Match transition duration
    }

    // Reset all button states
    this.specButtons.forEach(btn => btn.classList.remove('is-active'));
    this._activeButton = null;
  }

  showMobileSpecs(type) {
    // Always recreate the dialog on mobile to avoid stale content
    if (this.specsMobileDialog) {
      this.specsMobileDialog.remove();
      this.specsMobileDialog = null;
    }

    // Create mobile dialog for specs
    this.specsMobileDialog = document.createElement('dialog');
    this.specsMobileDialog.className = 'slideover-dialog';

    const closeButton = document.createElement('button');
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.className = 'slideover-dialog__close';
    closeButton.setAttribute('tabindex', '0');
    closeButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon icon-close">
        <line x1="15.5371" y1="8.46439" x2="8.46608" y2="15.5355" stroke="currentColor"/>
        <line x1="15.5371" y1="15.5357" x2="8.466" y2="8.46461" stroke="currentColor"/>
      </svg>
    `;
    closeButton.addEventListener('click', () => this.specsMobileDialog.close());

    const titleElement = document.createElement('h2');
    titleElement.className = 'slideover-dialog__title-text';
    titleElement.textContent = this.specsContainer?.getAttribute('data-title') || 'Product Specifications';

    const contentContainer = document.createElement('div');
    contentContainer.className = 'slideover-dialog__content';
    
    // Clone the specs container content
    const tempContainer = this.specsContainer?.cloneNode(true);
    
    // Apply filtering to the cloned content
    if (tempContainer && type) {
      this.filterSpecsContentInElement(tempContainer, type);
    }
    
    contentContainer.innerHTML = tempContainer?.innerHTML || '';

    // Remove the close button from the cloned content
    const closeButtonInContent = contentContainer.querySelector('[data-close]');
    if (closeButtonInContent) {
      closeButtonInContent.remove();
    }

    this.specsMobileDialog.appendChild(closeButton);
    this.specsMobileDialog.appendChild(titleElement);
    this.specsMobileDialog.appendChild(contentContainer);

    // Find the product-form element to append the dialog to
    const productForm =
      this.closest('product-form') || this.querySelector('product-form') || document.querySelector('product-form');

    if (productForm) {
      productForm.appendChild(this.specsMobileDialog);
    } else {
      this.appendChild(this.specsMobileDialog);
    }

    // Add close event listener
    this.specsMobileDialog.addEventListener('close', () => {
      document.body.style.overflow = '';

      // Return focus to spec button only if opened via keyboard
      if (this._keyboardOpen && this._lastTrigger) {
        setTimeout(() => {
          this._lastTrigger.focus();
        }, 0);
      } else {
        document.activeElement.blur()
      }

      // Reset button state to show specs text
      if (this.specButton) {
        this.specButton.classList.remove('is-active');
      }

      // Remove dialog from DOM completely after it's closed
      // Use a small timeout to allow the closing animation to complete
      setTimeout(() => {
        if (this.specsMobileDialog) {
          this.specsMobileDialog.remove();
          this.specsMobileDialog = null;
        }
      }, 300);
    });

    // Add keyboard event listener for accessibility
    this.specsMobileDialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.specsMobileDialog.close();
      }
    });

    // Show the dialog
    this.specsMobileDialog.showModal();
    document.body.style.overflow = 'hidden';

    // Immediately blur any focused element to prevent Safari's default focus for mouse interactions
    if (!this._keyboardOpen && document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    // Only focus the close button if opened via keyboard
    if (this._keyboardOpen) {
      setTimeout(() => {
        const closeBtn = this.specsMobileDialog.querySelector('.slideover-dialog__close');
        if (closeBtn) {
          closeBtn.focus();
        }
      }, 100);
    }
  }

  hideSpecs() {
    // For single variant products, always use desktop behavior
    if (this.isSingleVariantProduct()) {
      this.hideDesktopSpecs();
    } else if (this.isMobile) {
      if (this.specsMobileDialog) {
        this.specsMobileDialog.close();
      }
    } else {
      this.hideDesktopSpecs();
    }

    // Reset all button states
    this.specButtons.forEach(btn => btn.classList.remove('is-active'));
    this._activeButton = null;
    
    // Hide close button
    if (this.titleCloseButton) {
      this.titleCloseButton.style.display = 'none';
    }
    
    // Reset filtering
    this.resetSpecsContent();
  }

  updateGalleryStickiness() {
    if (window.matchMedia('(max-width: 767px)').matches && this.prevState !== 'mobile') {
      const marginBottom = parseFloat(getComputedStyle(this.absoluteContainer).marginBottom.replace('px', ''));
      this.productDataElement.style.paddingTop = `${this.absoluteContainer.clientHeight + marginBottom}px`;
      this.absoluteContainer.style.setProperty('position', `absolute`, 'important');

      const targetTop = this.targetElement.offsetTop;
      this.absoluteContainer.style.height = `${targetTop}px`;
      this.prevState = 'mobile';
    } else if (window.matchMedia('(min-width: 768px)').matches && this.prevState !== 'desktop') {
      this.productDataElement.style.paddingTop = '';
      this.absoluteContainer.style.setProperty('position', ``, 'important');
      this.absoluteContainer.style.height = '';
      this.prevState = 'desktop';
    }
  }

  filterSpecsContent(type) {
    if (!this.specsContainer || !type) return;
    
    const rows = this.specsContainer.querySelectorAll('.product-details-row');
    
    rows.forEach((row, index) => {
      if (type === 'dimensions') {
        // Show only the first row for dimensions
        row.style.display = index === 0 ? 'flex' : 'none';
      } else if (type === 'details') {
        // Show all rows except the first for details
        row.style.display = index === 0 ? 'none' : 'flex';
      }
    });
  }
  
  filterSpecsContentInElement(element, type) {
    if (!element || !type) return;
    
    const rows = element.querySelectorAll('.product-details-row');
    
    rows.forEach((row, index) => {
      if (type === 'dimensions') {
        // Show only the first row for dimensions
        if (index !== 0) {
          row.remove();
        }
      } else if (type === 'details') {
        // Show all rows except the first for details
        if (index === 0) {
          row.remove();
        }
      }
    });
  }
  
  resetSpecsContent() {
    if (!this.specsContainer) return;
    
    const rows = this.specsContainer.querySelectorAll('.product-details-row');
    rows.forEach(row => {
      row.style.display = 'flex';
    });
  }
}

customElements.define('product-container', ProductContainer);
